import React, { useEffect, useState } from "react";
import {
    Modal,
    ModalHeader,
    ModalBody,
    Card,
    CardBody,
    Row,
    Col,
    CardText,
    Button,
} from "reactstrap";
import Moment from "react-moment";
import { FaMessage } from "react-icons/fa6";
import { FiEdit } from "react-icons/fi";
import { useDispatch, useSelector } from "react-redux";
import createUserSelector from "store/users/selector";
import { BsExclamationCircle } from "react-icons/bs";
import { RxCrossCircled } from "react-icons/rx";
import axios from "axios";
import { callMessageNotification } from "store/Message/actions";
import MessageReply from "./MessageReply";
import socket from "socket/socket";
import MessageDetails from "./MessageDetails";

import './MessageModal.css'

const MessageModal = ({ messageNotifications, toggle }) => {
    const dispatch = useDispatch();
    const [isLoading, setIsLoading] = useState(false);

    const sortedNotifications = [...messageNotifications].sort(
        (a, b) => new Date(b.timestamp) - new Date(a.timestamp)
    );

    const handleClearAll = async (recieverId) => {
        setIsLoading(true);

        if (recieverId) {
            try {
                const response = await axios.delete(
                    `${process.env.REACT_APP_BACKEND_URL}/twilio/message-notifications-clear/${recieverId}`
                );

                if (response.status === 200) {
                    dispatch(callMessageNotification(true));
                }
            } catch (error) {
                console.log(error);
            }
        }

        setIsLoading(false);
    };

    return (
        <Modal isOpen toggle={toggle} size="lg" className="message-modal">
            <ModalHeader toggle={toggle} className="fw-semibold fs-5">
                Messages
            </ModalHeader>

            <ModalBody className="message-body">
                {sortedNotifications.length > 0 ? (
                    sortedNotifications.map((notification) => (
                        <NotificationCard
                            key={notification._id}
                            notification={notification}
                        />
                    ))
                ) : (
                    <NotMessageCard />
                )}

                <div className="text-end mt-3">
                    <Button
                        disabled={sortedNotifications.length === 0}
                        color="light"
                        size="sm"
                        onClick={() => handleClearAll("CameraAlarms")}
                    >
                        Clear All
                    </Button>
                </div>
            </ModalBody>
        </Modal>
    );
};

/* ========================
   Notification Card
======================== */

const NotificationCard = ({ notification }) => {
    const dispatch = useDispatch();

    const userSelector = useSelector(createUserSelector);
    const { allLoginCustomer } = userSelector;

    const [hoveredId, setHoveredId] = useState(null);
    const [isReplyOpen, setIsReplyOpen] = useState(false);
    const [showLessMessage, setShowLessMessage] = useState(false);

    const [localSnooze, setLocalSnooze] = useState(
        notification?.snooze30Days || false
    );

    useEffect(() => {
        setLocalSnooze(notification?.snooze30Days || false);
    }, [notification]);

    const handleRemoveMessage = async (messageId) => {
        try {
            const response = await axios.delete(
                `${process.env.REACT_APP_BACKEND_URL}/twilio/message-notifications-remove/${messageId}`
            );

            if (response.status === 200) {
                dispatch(callMessageNotification(true));
                socket.emit("message:delete", { messageId });
            }
        } catch (err) {
            console.log(err);
        }
    };

    const handleSnooz = async (snooze, messageContent) => {
        setLocalSnooze(snooze);

        const match = messageContent.match(/Camera (\S+) has/);
        const cameraNumber = match ? match[1] : null;

        if (!cameraNumber) return;

        try {
            await axios.post(
                `${process.env.REACT_APP_BACKEND_URL}/twilio/message-snooze`,
                {
                    snooze,
                    cameraNumber,
                }
            );

            dispatch(callMessageNotification(true));
        } catch (err) {
            console.log(err);
        }
    };

    return (
        <Card
            className="notification-card mb-2"
            onMouseEnter={() => setHoveredId(notification._id)}
            onMouseLeave={() => setHoveredId(null)}
        >
            <CardBody>

                <Row className="align-items-start">

                    {/* icon */}
                    <Col xs="1">
                        <BsExclamationCircle className="text-danger mt-1 fs-4" />
                    </Col>

                    {/* content */}
                    <Col xs="11">

                        {/* top row */}
                        <div className="d-flex justify-content-between">

                            <div className="message-text">

                                {!showLessMessage ? (
                                    <>
                                        {notification.messageContent.substring(0, 80)}
                                        {notification.messageContent.length > 80 && (
                                            <span
                                                className="text-primary fw-semibold ms-1 cursor"
                                                onClick={() => setShowLessMessage(true)}
                                            >
                                                more
                                            </span>
                                        )}
                                    </>
                                ) : (
                                    notification.messageContent
                                )}

                            </div>

                            <small className="text-muted">
                                <Moment fromNow>{notification.timestamp}</Moment>
                            </small>


                            <Col xs="2" className="text-end">

                                {hoveredId === notification._id && (
                                    <RxCrossCircled
                                        className="text-danger cursor"
                                        size={18}
                                        onClick={() =>
                                            handleRemoveMessage(notification._id)
                                        }
                                    />
                                )}

                            </Col>
                        </div>


                        {/* snooze */}
                        {notification.MessageType === "CameraAlarms" && (
                            <div className="d-flex align-items-center mt-2">

                                <span className="fw-semibold me-2 small">
                                    Snooze 30 Days
                                </span>

                                <input
                                    type="checkbox"
                                    className="snooze-checkbox"
                                    checked={localSnooze}
                                    onChange={(e) =>
                                        handleSnooz(e.target.checked, notification.messageContent)
                                    }
                                />

                            </div>
                        )}


                        {/* footer row */}
                        <Row className="mt-2 align-items-center">

                            <Col xs="5" className="text-primary small fw-semibold">
                                {notification.company || notification.customerName}
                            </Col>

                            <Col xs="5" className="small text-truncate">
                                {notification.siteAddress}
                            </Col>

                            <Col xs="2" className="text-end">
                                {notification?.cameraIP && (
                                    <a
                                        href={`http://${notification.cameraIP}${notification.port ? `:${notification.port}` : ""
                                            }`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        <FiEdit
                                            size={16}
                                            style={{ cursor: "pointer", color: "#28a745" }}
                                        />
                                    </a>
                                )}
                            </Col>

                        </Row>


                        {/* reply + details */}
                        {notification.MessageType !== "CameraAlarms" && notification.MessageType !== "AppArmRequest" && (
                            <Row className="mt-2">

                                <Col>
                                    <MessageReply
                                        notification={notification}
                                        allLoginCustomer={allLoginCustomer}
                                        isReplyOpen={isReplyOpen}
                                        setIsReplyOpen={setIsReplyOpen}
                                    />
                                </Col>

                                <Col className="text-end">
                                    <MessageDetails
                                        notification={notification}
                                        allLoginCustomer={allLoginCustomer}
                                        isReplyOpen={isReplyOpen}
                                        setIsReplyOpen={setIsReplyOpen}
                                    />
                                </Col>

                            </Row>
                        )}

                    </Col>

                </Row>

            </CardBody>
        </Card>
    );
};





/* ========================
   Empty State
======================== */

const NotMessageCard = () => {
    return (
        <CardBody className="text-center py-5 empty-state">
            <FaMessage size={40} />
            <CardText className="mt-3 fw-semibold">
                No Messages Yet
            </CardText>
        </CardBody>
    );
};

export default MessageModal;
